import { z } from "zod";
import { REDACTED_PLACEHOLDER } from "./display-scope.js";

/**
 * Contracts SỔ HIẾU HỈ HAI CHIỀU — US-E20.F2-0208-S02 (@UC9327–@UC9330).
 * 🔴 Tiền = `string` BigInt minor (KHÔNG number/float lộ ra JSON — B1). Mọi schema `.strict()`.
 *
 * 🔴🔴 NGHĨA VỤ XÃ HỘI, KHÔNG PHẢI NỢ: `netMinor` là "có đi có lại" — KHÔNG phải khoản phải-thu.
 * net < 0 ⇒ MÌNH nên đáp lễ (gợi ý, không ép). Contract này KHÔNG có field nào kiểu "dueDate" /
 * "status: overdue" — cố ý: máy không được biến đạo lý thành hoá đơn.
 *
 * Mirror pattern `huiLineResponseSchema` / `loanResponseSchema`.
 */

const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;
/** net CÓ DẤU: âm = mình đang nợ đáp lễ. */
const SIGNED_MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const GIFT_EVENT_TYPE_VALUES = [
  "wedding",
  "funeral",
  "full_month",
  "housewarming",
  "other",
] as const;
export const GIFT_DIRECTION_VALUES = ["given", "received"] as const;
/** member = cá nhân tự bỏ tiền túi (🔴 quỹ KHÔNG động) · fund = quỹ nhóm đứng ra mừng/nhận. */
export const GIFT_PAYER_VALUES = ["member", "fund"] as const;
export const GIFT_VISIBILITY_VALUES = ["public", "private", "sensitive"] as const;

export const giftEventTypeSchema = z.enum(GIFT_EVENT_TYPE_VALUES);
export const giftDirectionSchema = z.enum(GIFT_DIRECTION_VALUES);
export const giftPayerSchema = z.enum(GIFT_PAYER_VALUES);
export const giftVisibilitySchema = z.enum(GIFT_VISIBILITY_VALUES);

/* ─────────────────────────────── INPUT (validate biên) ─────────────────────────────── */

export const recordGiftSchema = z
  .object({
    /** Tên đối phương LUÔN có (người NGOÀI nhóm chỉ có tên). */
    counterpartyName: z.string().min(1).max(200),
    /** Đối phương TRONG nhóm ⇒ groupMemberId; NGOÀI nhóm ⇒ vắng/null. */
    counterpartyMemberId: z.string().uuid().nullish(),
    eventType: giftEventTypeSchema,
    eventDate: z.string().regex(ISO_DATE_PATTERN, "eventDate phải là YYYY-MM-DD"),
    direction: giftDirectionSchema,
    // 🔴 amount > 0 (refine chạy KỂ CẢ khi regex fail ⇒ tự kiểm pattern trước BigInt()).
    amountMinor: z
      .string()
      .regex(MONEY_INTEGER_STRING_PATTERN)
      .refine(
        (v) => MONEY_INTEGER_STRING_PATTERN.test(v) && BigInt(v) > 0n,
        "amountMinor phải > 0",
      ),
    /** Mặc định 'member' — cá nhân tiền túi ⇒ KHÔNG bút toán CASH nào. */
    payer: giftPayerSchema.optional(),
    visibility: giftVisibilitySchema.optional(),
    note: z.string().max(500).nullish(),
    idempotencyKey: z.string().min(1).max(200),
  })
  .strict();
export type RecordGiftRequest = z.infer<typeof recordGiftSchema>;

/* ─────────────────────────────── RESPONSE ─────────────────────────────── */

export const giftEventResponseSchema = z
  .object({
    id: z.string().uuid(),
    counterpartyName: z.string(),
    counterpartyMemberId: z.string().uuid().nullable(),
    eventType: giftEventTypeSchema,
    eventDate: z.string().regex(ISO_DATE_PATTERN),
  })
  .strict();
export type GiftEventResponse = z.infer<typeof giftEventResponseSchema>;

export const giftEntryResponseSchema = z
  .object({
    id: z.string().uuid(),
    giftEventId: z.string().uuid(),
    direction: giftDirectionSchema,
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    payer: giftPayerSchema,
    /** Bút toán GHI NHỚ (GIFT_GIVEN/GIFT_RECEIVED) — LUÔN có, 🔴 0 chân CASH. */
    ledgerTxId: z.string().uuid(),
    /** Bút toán TIỀN THẬT của quỹ (EXPENSE/OTHER_INCOME) — 🔴 null khi cá nhân tự bỏ tiền túi. */
    cashTxId: z.string().uuid().nullable(),
    visibility: giftVisibilitySchema,
    note: z.string().nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();
export type GiftEntryResponse = z.infer<typeof giftEntryResponseSchema>;

/** Lượt lễ + dịp (trả về sau khi ghi). */
export const recordGiftResponseSchema = z
  .object({
    entry: giftEntryResponseSchema,
    event: giftEventResponseSchema,
  })
  .strict();
export type RecordGiftResponse = z.infer<typeof recordGiftResponseSchema>;

/**
 * NET theo đối phương (VIEW — đọc từ các lượt đã ghi, KHÔNG lưu bảng net).
 * `netMinor` CÓ DẤU: dương = mình đi lễ nhiều hơn (họ nên đáp) · âm = 🔴 MÌNH nên đáp lễ.
 * `suggestedReciprocityMinor` = GỢI Ý số đáp lễ (|net| khi âm; 0 khi không nợ) — GỢI Ý, KHÔNG ÉP.
 *
 * DISCRIMINATED UNION theo `redacted` (mirror `display-scope`): nhánh redact KHÔNG có số thật
 * (literal '•••') ⇒ FAIL-LOUD nếu server lỡ ghép số thật với redacted:true. Redaction quyết định bởi
 * `resolveVisibility` (@goppy/domain — 1 NGUỒN DUY NHẤT, không whitelist thứ hai).
 */
const shownReciprocalNetSchema = z
  .object({
    counterpartyName: z.string(),
    counterpartyMemberId: z.string().uuid().nullable(),
    redacted: z.literal(false),
    totalGivenMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    totalReceivedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    netMinor: z.string().regex(SIGNED_MONEY_INTEGER_STRING_PATTERN),
    /** true ⇒ MÌNH đang nợ đáp lễ (nghĩa vụ XÃ HỘI — KHÔNG chặn rời nhóm/xoá tài khoản). */
    owesReciprocity: z.boolean(),
    suggestedReciprocityMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    entryCount: z.number().int().min(0),
  })
  .strict();

const redactedReciprocalNetSchema = z
  .object({
    counterpartyName: z.string(),
    counterpartyMemberId: z.string().uuid().nullable(),
    redacted: z.literal(true),
    totalGivenMinor: z.literal(REDACTED_PLACEHOLDER),
    totalReceivedMinor: z.literal(REDACTED_PLACEHOLDER),
    netMinor: z.literal(REDACTED_PLACEHOLDER),
    /** Fail-closed: người không được xem KHÔNG bị suy ra "ai nợ đáp lễ ai". */
    owesReciprocity: z.literal(false),
    suggestedReciprocityMinor: z.literal(REDACTED_PLACEHOLDER),
    entryCount: z.number().int().min(0),
  })
  .strict();

export const reciprocalNetResponseSchema = z.discriminatedUnion("redacted", [
  shownReciprocalNetSchema,
  redactedReciprocalNetSchema,
]);
export type ReciprocalNetResponse = z.infer<typeof reciprocalNetResponseSchema>;

export const reciprocalNetListResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    counterparties: z.array(reciprocalNetResponseSchema),
  })
  .strict();
export type ReciprocalNetListResponse = z.infer<typeof reciprocalNetListResponseSchema>;

/** Config key nhắc đáp lễ — 🔴 DEFAULT TẮT (0 regress: không tự phun notification). */
export const RECIPROCITY_REMINDER_ENABLED_KEY = "reciprocity_reminder_enabled";
export const RECIPROCITY_REMINDER_ENABLED_DEFAULT = false;
